/**
 * Settings Categories Definition
 * Defines the UI structure for navigating settings
 */

import type { BillingTier, SettingsLevel } from './types';
import type { NavColorScheme } from '../navigation/types';

export interface SettingsCategory {
  id: string;
  label: string;
  icon: string; // Lucide icon name
  description: string;
  subcategories: SettingsSubcategory[];
  minTier?: BillingTier;
  /** Which settings levels can see this category. If undefined, visible at all levels except 'personal'. */
  visibleAtLevels?: SettingsLevel[];
  /** Color scheme matching the related navigation section */
  colorScheme?: NavColorScheme;
  /** Permission codes required to see this category (OR logic — any match grants access).
   *  Mirrors the DB `navigation_items.required_permissions` for defense-in-depth. */
  requiredPermissions?: string[];
}

export interface SettingsSubcategory {
  id: string;
  label: string;
  description: string;
}

// These category permissions already allowed supervisors to open their settings pages.
// Any category without one of them is admin-only; in particular settings.admin and
// integrations.manage never inherit the generic supervisor route.
const SUPERVISOR_CATEGORY_PERMISSIONS = new Set([
  'settings.view', 'settings.edit', 'reports.view', 'calls.admin',
]);

export function settingsCategoryRequiresAdmin(category: SettingsCategory): boolean {
  return !category.requiredPermissions?.some((permission) => SUPERVISOR_CATEGORY_PERMISSIONS.has(permission));
}

export const SETTINGS_CATEGORIES: SettingsCategory[] = [
  {
    id: 'general',
    requiredPermissions: ['settings.view'],
    label: 'General',
    icon: 'Settings',
    description: 'Basic platform settings',
    colorScheme: 'blue', // Platform group color
    subcategories: [
      {
        id: 'organization',
        label: 'Organization',
        description: 'Agency and department details',
      },
      {
        id: 'localization',
        label: 'Localization',
        description: 'Timezone, date, and time formats',
      },
    ],
    // Organization settings only - not for personal
    visibleAtLevels: ['platform', 'agency', 'department'],
  },
  {
    id: 'branding',
    requiredPermissions: ['settings.edit'],
    label: 'Branding',
    icon: 'Palette',
    description: 'Customize look and feel',
    colorScheme: 'blue', // Platform group color
    subcategories: [
      {
        id: 'identity',
        label: 'Brand Identity',
        description: 'Logo, colors, and company info',
      },
      {
        id: 'theme',
        label: 'Theme',
        description: 'Color schemes and visual style',
      },
      {
        id: 'custom',
        label: 'Custom Assets',
        description: 'Custom CSS and advanced styling',
      },
    ],
    minTier: 'professional',
    // Organization branding only
    visibleAtLevels: ['platform', 'agency', 'department'],
  },
  {
    id: 'ai',
    requiredPermissions: ['settings.admin'],
    label: 'AI & ML',
    icon: 'Brain',
    description: 'Artificial intelligence configuration',
    colorScheme: 'cyan', // Distinct AI color
    subcategories: [
      {
        id: 'models',
        label: 'Models',
        description: 'AI model selection and configuration',
      },
      {
        id: 'performance',
        label: 'Performance',
        description: 'Timeouts and optimization',
      },
      {
        id: 'analysis',
        label: 'Analysis',
        description: 'Content analysis thresholds',
      },
    ],
    minTier: 'starter',
    // AI settings are organization-level
    visibleAtLevels: ['platform', 'agency', 'department'],
  },
  {
    id: 'features',
    requiredPermissions: ['settings.admin'],
    label: 'Features',
    icon: 'Zap',
    description: 'Enable or disable platform features',
    colorScheme: 'purple', // Admin group color
    subcategories: [
      {
        id: 'transcription',
        label: 'Transcription',
        description: 'Live transcription options',
      },
      {
        id: 'search',
        label: 'Search',
        description: 'Search and vector features',
      },
      {
        id: 'collaboration',
        label: 'Collaboration',
        description: 'Canvas, maps, file sharing',
      },
      {
        id: 'calls',
        label: 'Calls',
        description: 'Call handling options',
      },
      {
        id: 'training',
        label: 'Training',
        description: 'Training and simulation',
      },
      {
        // officer-bithuman: the platform-wide on/off switch and the default face (components/settings/OfficerAvatarSettings).
        id: 'officer-avatars',
        label: 'Officer avatars',
        description: 'Animated faces for the AI officer and for officers who animate their voice',
      },
    ],
    // Feature toggles are organization-level
    visibleAtLevels: ['platform', 'agency', 'department'],
  },
  {
    id: 'ui',
    requiredPermissions: ['settings.view'],
    label: 'User Experience',
    icon: 'Layout',
    description: 'Interface and interaction settings',
    colorScheme: 'blue', // Platform group color
    subcategories: [
      {
        id: 'appearance',
        label: 'Appearance',
        description: 'Visual effects and layout',
      },
      {
        id: 'behavior',
        label: 'Behavior',
        description: 'Autosave and defaults',
      },
      {
        id: 'notifications',
        label: 'Notifications',
        description: 'Alert preferences',
      },
      {
        id: 'transcript',
        label: 'Transcript Display',
        description: 'How transcripts are shown',
      },
      {
        id: 'navigation',
        label: 'Navigation',
        description: 'Home page preference',
      },
      {
        id: 'call-layout',
        label: 'Call Page Layout',
        description: 'Control which elements are visible during calls',
      },
    ],
    // UI preferences apply to all levels including personal
    visibleAtLevels: ['personal', 'platform', 'agency', 'department'],
  },
  {
    id: 'reports',
    requiredPermissions: ['reports.view', 'settings.edit'],
    label: 'Reports',
    icon: 'FileText',
    description: 'Report generation and templates',
    colorScheme: 'amber', // Library group color
    subcategories: [
      {
        id: 'templates',
        label: 'Templates',
        description: 'Default report templates',
      },
      {
        id: 'export',
        label: 'Export',
        description: 'Export formats and options',
      },
      {
        id: 'automation',
        label: 'Automation',
        description: 'Automatic report generation',
      },
    ],
    // Report settings are organization-level
    visibleAtLevels: ['platform', 'agency', 'department'],
  },
  {
    id: 'calls',
    requiredPermissions: ['calls.admin', 'settings.edit'],
    label: 'Calls',
    icon: 'Phone',
    description: 'Call handling and recording',
    colorScheme: 'emerald', // Calls group color
    subcategories: [
      {
        id: 'limits',
        label: 'Limits',
        description: 'Duration and capacity limits',
      },
      {
        id: 'behavior',
        label: 'Behavior',
        description: 'Timeout and idle settings',
      },
      {
        id: 'recording',
        label: 'Recording',
        description: 'Recording settings',
      },
      {
        id: 'livekit',
        label: 'LiveKit Infrastructure',
        description: 'Video/audio server (cloud or self-hosted)',
      },
    ],
    // Call settings are organization-level
    visibleAtLevels: ['platform', 'agency', 'department'],
  },
  {
    id: 'integrations',
    requiredPermissions: ['integrations.manage'],
    label: 'Integrations',
    icon: 'Plug',
    description: 'Third-party service connections',
    colorScheme: 'purple', // Admin group color
    subcategories: [
      {
        id: 'analytics',
        label: 'Analytics',
        description: 'Google Analytics and tracking',
      },
      {
        id: 'monitoring',
        label: 'Monitoring',
        description: 'Error tracking and observability',
      },
      {
        id: 'webhooks',
        label: 'Webhooks',
        description: 'Event notifications',
      },
      {
        id: 'api',
        label: 'API',
        description: 'API configuration',
      },
      {
        id: 'evidence_com',
        label: 'Evidence.com',
        description: 'Upload signed reports to Axon Evidence.com',
      },
    ],
    minTier: 'professional',
    // Integrations are organization-level
    visibleAtLevels: ['platform', 'agency', 'department'],
  },
  {
    id: 'security',
    requiredPermissions: ['settings.admin'],
    label: 'Security',
    icon: 'Shield',
    description: 'Security and access controls',
    colorScheme: 'purple', // Admin group color
    subcategories: [
      {
        id: 'authentication',
        label: 'Authentication',
        description: 'Login and session settings',
      },
      {
        id: 'permissions',
        label: 'Permissions',
        description: 'Role-based access',
      },
      {
        id: 'audit',
        label: 'Audit Log',
        description: 'Activity and change logs',
      },
    ],
    minTier: 'professional',
    // Security settings are organization-level
    visibleAtLevels: ['platform', 'agency', 'department'],
  },
  // NOTE: Users & Permissions removed - use /adminpanel/access-control instead
  {
    id: 'billing',
    requiredPermissions: ['billing.view'],
    label: 'Billing',
    icon: 'CreditCard',
    description: 'Subscription and payment management',
    colorScheme: 'purple', // Admin group color
    subcategories: [
      {
        id: 'plan',
        label: 'Current Plan',
        description: 'Subscription details',
      },
      {
        id: 'usage',
        label: 'Usage',
        description: 'Usage metrics and limits',
      },
      {
        id: 'invoices',
        label: 'Invoices',
        description: 'Payment history',
      },
    ],
    minTier: 'starter',
    // Billing is platform and agency level only
    visibleAtLevels: ['platform', 'agency'],
  },
];

/**
 * Get category by ID
 */
export function getCategoryById(categoryId: string): SettingsCategory | undefined {
  return SETTINGS_CATEGORIES.find((cat) => cat.id === categoryId);
}

/**
 * Get subcategory by category and subcategory ID
 */
export function getSubcategoryById(
  categoryId: string,
  subcategoryId: string
): SettingsSubcategory | undefined {
  const category = getCategoryById(categoryId);
  return category?.subcategories.find((sub) => sub.id === subcategoryId);
}

/**
 * Check if a category is visible at a specific settings level
 */
export function isCategoryVisibleAtLevel(
  category: SettingsCategory,
  level: SettingsLevel
): boolean {
  // If no visibleAtLevels defined, default to all organizational levels (not personal)
  if (!category.visibleAtLevels) {
    return level !== 'personal';
  }
  return category.visibleAtLevels.includes(level);
}

/**
 * Categories whose feature is not built yet: hidden from every tier, role and level.
 * The definitions stay in SETTINGS_CATEGORIES; only the filters below hide them.
 */
const HIDDEN_CATEGORY_IDS: readonly string[] = ['billing'];

export function isCategoryHidden(category: SettingsCategory): boolean {
  return HIDDEN_CATEGORY_IDS.includes(category.id);
}

/**
 * Check if a category is accessible based on billing tier, user role, and settings level
 * Admins and platform admins bypass tier restrictions
 */
export function isCategoryAccessible(
  category: SettingsCategory,
  userTier: BillingTier,
  userRole?: string,
  level?: SettingsLevel
): boolean {
  if (isCategoryHidden(category)) {
    return false;
  }

  // First check level visibility if provided
  if (level && !isCategoryVisibleAtLevel(category, level)) {
    return false;
  }

  // Admins always have full access regardless of tier
  if (userRole && ['admin', 'admin-platform', 'admin-tenant', 'platform-admin', 'owner', 'executive'].includes(userRole)) {
    return true;
  }

  if (!category.minTier) return true;

  const tierOrder: BillingTier[] = ['trial', 'starter', 'professional', 'enterprise'];
  const categoryTierIndex = tierOrder.indexOf(category.minTier);
  const userTierIndex = tierOrder.indexOf(userTier);

  return userTierIndex >= categoryTierIndex;
}

/**
 * Get all categories accessible for a billing tier and user role
 */
export function getAccessibleCategories(userTier: BillingTier, userRole?: string): SettingsCategory[] {
  return SETTINGS_CATEGORIES.filter((cat) => isCategoryAccessible(cat, userTier, userRole));
}
